'use client';
import { DownloadIcon, PlusIcon } from '@c/icons';
import EmptyBox from '@i/empty-box.svg';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@ui/Button';
import { PaginatorClient } from '@ui/Paginator';
import SafeImage from '@ui/SafeImage';
import Select from '@ui/Select';
import Spinner from '@ui/Spinner';
import Toggle from '@ui/Toggle';
import { exportListings } from '@util/firestore/listing';
import {
  bumpProduct,
  getProductViews,
  getSellerListingsPaginated,
  updateProductByKeys,
} from '@util/firestore/products';
import { searchSellerProductsServer } from '@util/firestore/products/products.typesense';
import useDebounce from '@util/hooks/useDebounce';
import { formatCurrency } from '@util/index';
import { logError } from '@util/logError';
import { getProductSlug } from '@util/urlHelpers';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { deleteField } from 'firebase/firestore';
import {
  ChevronsUpDown,
  EllipsisVertical,
  ExternalLink,
  Eye,
  Heart,
} from 'lucide-react';
import {
  Condition,
  isVehicleCategory,
  ProductDocument,
  productSchema,
} from 'models/product';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { IoDocumentTextOutline, IoSearchOutline } from 'react-icons/io5';
import EmptyState from '../EmptyState';
import DeleteListingModal from './components/DeleteListingModal';
import { ListingActionsDrawer } from './components/ListingActionsDrawer';
import PriceDropModal from './components/PriceDropModal';
import { PromoteModal } from './components/PromoteModal';
import SmartPricingModal from './components/SmartPricingModal';
import SmartPricingOffModal from './components/SmartPricingOffModal';
import { SORT_OPTIONS, SortModal } from './components/SortModal';

export type Availability =
  | 'onSale'
  | 'inStock'
  | 'draft'
  | 'sold'
  | 'auction'
  | 'notSold'
  | 'paymentFailed'
  | 'all';

export const SoldOverlay = ({ variant }: { variant: 'auction' | 'sold' }) => (
  <div className="pointer-events-none absolute top-0 flex h-full w-full items-center justify-center @container">
    <div className=" flex w-full flex-col items-center justify-center bg-brand-secondary py-[0.8rem] font-semibold text-white opacity-80">
      {variant === 'auction' ? 'ENDED' : 'SOLD'}
    </div>
  </div>
);

export const DraftOverlay = ({ variant }: { variant: 'auction' | 'draft' }) => (
  <div className="absolute left-0 top-0 h-full w-full rounded-2xl bg-brand-lightest-black bg-opacity-50">
    <div className="absolute left-0 top-0 flex h-full w-full flex-col items-center justify-center">
      <div
        className={`w-full ${
          variant === 'auction' ? 'bg-orange-500' : 'bg-yellow-500'
        } px-[1.6rem] py-[1.4rem] text-center font-semibold text-brand-white opacity-50`}
      >
        {variant === 'auction' ? 'Auction Starting Soon' : 'Draft'}
      </div>
    </div>
  </div>
);

export const AnotherPersonCartOverlay = ({ count }: { count: number }) => (
  <div className="absolute left-6 mt-6 h-[3rem] rounded-full bg-brand-secondary px-4">
    <div className="flex h-full items-center justify-center">
      <div className="whitespace-nowrap text-center text-[1.4rem] font-semibold uppercase text-brand-white sm:text-[1.5rem]">
        {count} {count === 1 ? 'person has' : 'people have'} this in their cart
      </div>
    </div>
  </div>
);

const getAuctionTimeLeft = (endTime: number, now: number) => {
  const diff = Math.max(endTime - now, 0);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const formatAuctionStart = (startTime: number) => {
  const date = new Date(startTime);
  const pad = (n: number) => String(n).padStart(2, '0');
  const hours = date.getHours();
  const period = hours >= 12 ? 'PM' : 'AM';
  return `${pad(date.getMonth() + 1)}/${pad(
    date.getDate()
  )}/${date.getFullYear()} ${hours % 12 || 12}:${pad(
    date.getMinutes()
  )} ${period}`;
};

const AuctionEndsOverlay = ({
  timeLeft,
  compact,
}: {
  timeLeft: string;
  compact?: boolean;
}) => (
  <div className="absolute bottom-0 left-0 flex w-full items-center justify-center gap-2 bg-[#1e7a1e] px-3 py-2 text-white">
    <span className="text-body-sm font-semibold">
      {compact ? timeLeft : `Ends ${timeLeft}`}
    </span>
  </div>
);

const formatListedDate = (timestamp?: number) => {
  if (!timestamp) return '';
  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
  });
};

const PurchaseEntryLink = ({ entryId }: { entryId: string }) => (
  <Link
    href={`/dashboard/purchase-entry/${entryId}`}
    target="_blank"
    className="flex w-fit shrink-0 items-center gap-2 whitespace-nowrap rounded-[6px] border border-[#d9d9d9] bg-[#f6f6f6] px-4 py-1 text-label font-medium text-[#444] transition-colors hover:bg-light-grey"
  >
    Purchase Entry
    <ExternalLink className="size-6" />
  </Link>
);

const ListingCardList = ({
  product,
  setSmartPricingModalOpen,
  setSelectedProduct,
  setDeleteListingModalOpen,
  setSmartPricingOffModalOpen,
  setPromoteModalOpen,
  setActionsDrawerOpen,
}: {
  product: ProductDocument;
  setSmartPricingModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedProduct: React.Dispatch<
    React.SetStateAction<ProductDocument | null>
  >;
  setDeleteListingModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSmartPricingOffModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setPromoteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setActionsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [now] = useState(() => Date.now()); // fixes eslint(react-hooks/purity)

  const { data: views } = useQuery({
    queryKey: ['views', product.id],
    queryFn: () => getProductViews(product.id),
    enabled: !!product.id,
  });

  const queryClient = useQueryClient();
  const { showSuccessToast } = useToastContext();

  const handleSmartPricing = () => {
    setSelectedProduct(product);

    if (product.floor_price) {
      setSmartPricingOffModalOpen(true);
    } else {
      setSmartPricingModalOpen(true);
    }
  };

  const handleDeleteListing = () => {
    setDeleteListingModalOpen(true);
    setSelectedProduct(product);
  };

  const handlePromote = () => {
    setPromoteModalOpen(true);
    setSelectedProduct(product);
  };

  const handleProductBump = async () => {
    await bumpProduct(product.id);
    queryClient.invalidateQueries({ queryKey: ['listings'] });
    showSuccessToast('Successfully bumped listing!');
  };

  const hasProductStock = !product.integration_info && !product.out_of_stock;

  const getDiscountPercentage = (newPrice: number, ogPrice: number) =>
    Math.round(((ogPrice - newPrice) * 100) / ogPrice);

  const sizeLabel = product.size?.letter;

  const isLiveAuction =
    product.is_auction &&
    !product.is_draft &&
    !product.out_of_stock &&
    (product.start_time ?? 0) <= now &&
    (product.end_time ?? 0) > now;
  const isScheduledAuction =
    product.is_auction && !product.is_draft && (product.start_time ?? 0) > now;
  const auctionTimeLeft = getAuctionTimeLeft(product.end_time ?? 0, now);
  const auctionStartLabel = formatAuctionStart(product.start_time ?? 0);

  return (
    <div>
      <div className="block lg:hidden">
        <div className="flex flex-col gap-6 rounded-[24px] bg-white p-6">
          <div className="flex gap-6">
            <Link
              href={getProductSlug(product)}
              className="relative size-[100px] shrink-0 overflow-hidden rounded-[16px]"
            >
              <SafeImage
                className="size-full rounded-[16px] object-cover"
                width={300}
                height={300}
                alt={product.title + 'image'}
                key={product.thumbnail}
                src={product.thumbnail}
              />
              {product.out_of_stock &&
                !product.is_draft &&
                !product.is_auction && <SoldOverlay variant={'sold'} />}
              {product.is_auction &&
                product.end_time! < now &&
                !product.is_draft &&
                product.out_of_stock && <SoldOverlay variant={'auction'} />}
              {product.is_draft && <DraftOverlay variant="draft" />}
              {isLiveAuction && (
                <AuctionEndsOverlay timeLeft={auctionTimeLeft} compact />
              )}
            </Link>
            <div className="flex flex-1 flex-col gap-2">
              {isLiveAuction ? (
                <span className="text-label font-medium text-[#1e7a1e]">
                  Live auction
                </span>
              ) : null}
              {isScheduledAuction ? (
                <span className="text-label font-medium text-brand-secondary">
                  Auction start at {auctionStartLabel}
                </span>
              ) : null}
              <span className="text-body-sm font-semibold">
                {product.title}
              </span>
              <div className="flex flex-wrap items-center gap-3 text-label font-medium text-[#7f7f7f]">
                <span>{product.condition}</span>
                {sizeLabel ? (
                  <React.Fragment>
                    <div className="size-2 rounded-full bg-[#7f7f7f]" />
                    <span>Size: {sizeLabel}</span>
                  </React.Fragment>
                ) : null}
                <div className="size-2 rounded-full bg-[#7f7f7f]" />
                <span>QTY: {product.stock}</span>
              </div>
              {product.sku ? (
                <span className="text-label font-medium text-[#7f7f7f]">
                  SKU: {product.sku}
                </span>
              ) : null}
              {product.on_sale ? (
                <div className="flex flex-wrap items-center gap-3 text-body-md font-semibold">
                  <span className="text-brand-secondary">
                    {formatCurrency(product.price)}
                  </span>
                  <span className="text-[#7f7f7f] line-through">
                    {formatCurrency(product.og_price)}
                  </span>
                  <span className="rounded-xl bg-brand-secondary px-3 py-1 text-label font-semibold text-white">
                    {getDiscountPercentage(product.price, product.og_price)}%
                    OFF
                  </span>
                </div>
              ) : (
                <span className="text-body-md font-semibold">
                  {formatCurrency(product.price)}
                </span>
              )}
              {Boolean(product.floor_price) ? (
                <span className="w-fit rounded-2xl bg-[#f9e8e8] px-4 py-1 text-label font-medium text-brand-secondary">
                  Floor Price: {formatCurrency(product.floor_price!)}
                </span>
              ) : null}
            </div>
            <button
              type="button"
              className="flex size-12 shrink-0 items-center justify-center text-black"
              onClick={() => {
                setSelectedProduct(product);
                setActionsDrawerOpen(true);
              }}
            >
              <EllipsisVertical className="size-9" />
            </button>
          </div>
          <div className="flex items-center justify-between gap-4">
            {product.purchase_entry_id ? (
              <PurchaseEntryLink entryId={product.purchase_entry_id} />
            ) : (
              <div />
            )}
            <div className="flex items-center gap-6 text-label font-medium text-[#7f7f7f]">
              {Boolean(product.date_added) ? (
                <span className="whitespace-nowrap">
                  Listed on {formatListedDate(product.date_added)}
                </span>
              ) : null}
              {views?.views ? (
                <span className="flex items-center gap-2">
                  <Eye className="size-7" />
                  {views.views + 1}
                </span>
              ) : null}
              <span className="flex items-center gap-2">
                <Heart className="size-7" />
                {product.favorite_count}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden h-full w-full gap-5 rounded-[24px] bg-white p-4 lg:flex lg:flex-row lg:items-center">
        <div className="flex w-full gap-6">
          <Link
            href={getProductSlug(product)}
            className="relative size-[200px] shrink-0 overflow-hidden rounded-[16px]"
          >
            <SafeImage
              className="size-[200px] rounded-[16px] object-cover"
              width={1920}
              height={1080}
              alt={'product image for ' + product.title}
              key={product.thumbnail}
              src={product.thumbnail}
            />
            {product.out_of_stock &&
              !product.is_draft &&
              !product.is_auction && <SoldOverlay variant={'sold'} />}
            {product.is_auction &&
              product.end_time! < now &&
              !product.is_draft &&
              product.out_of_stock && <SoldOverlay variant={'auction'} />}
            {product.is_draft && <DraftOverlay variant="draft" />}
            {isLiveAuction && <AuctionEndsOverlay timeLeft={auctionTimeLeft} />}
          </Link>

          <div className="flex-1 flex-col justify-between p-4">
            <div className="flex h-full w-full flex-col justify-between">
              <div className="flex flex-col gap-6">
                <div className="flex w-full items-start lg:justify-between">
                  <div className="flex flex-col">
                    {isLiveAuction ? (
                      <span className="text-body-sm font-medium text-[#1e7a1e]">
                        Live auction
                      </span>
                    ) : null}
                    {isScheduledAuction ? (
                      <span className="text-body-sm font-medium text-brand-secondary">
                        Auction start at {auctionStartLabel}
                      </span>
                    ) : null}
                    <span className="text-body-lg font-semibold">
                      {product.title}
                    </span>
                    <div className="flex items-center gap-2 text-body-sm font-medium text-[#7f7f7f]">
                      <span>{product.condition}</span>
                      <div className="size-2 rounded-full bg-[#7f7f7f]" />
                      <span>QTY: {product.stock}</span>
                    </div>
                    {product.sku ? (
                      <span className="text-body-sm font-medium text-[#7f7f7f]">
                        SKU: {product.sku}
                      </span>
                    ) : null}
                  </div>

                  {!product.integration_info &&
                    !product.out_of_stock &&
                    !product.is_auction && (
                      <div className="flex flex-col items-end gap-4">
                        <div className="hidden items-center gap-x-6 text-body-sm font-semibold lg:flex">
                          <span className="text-black">Smart Pricing</span>
                          <Toggle
                            enabled={Boolean(product.floor_price)}
                            onChange={handleSmartPricing}
                          />
                        </div>
                        {Boolean(product.floor_price) ? (
                          <span className="rounded-2xl bg-[#f9e8e8] px-4 py-1 text-body-sm font-medium text-brand-secondary">
                            Floor Price: {formatCurrency(product.floor_price!)}
                          </span>
                        ) : null}
                      </div>
                    )}
                </div>
                <div className="flex flex-col items-start gap-2">
                  {product.on_sale ? (
                    <div className="rounded-xl bg-brand-secondary px-3 py-1 text-label font-semibold text-white">
                      {getDiscountPercentage(product.price, product.og_price)}%
                      OFF
                    </div>
                  ) : null}
                  <div className="flex items-center text-body-xl font-semibold">
                    {product.on_sale ? (
                      <div className="flex items-center gap-4">
                        <p className="font-bold text-brand-secondary">
                          {formatCurrency(product.price)}
                        </p>
                        <p className="text-[#7f7f7f] line-through">
                          {formatCurrency(product.og_price)}
                        </p>
                      </div>
                    ) : (
                      <p>{formatCurrency(product.price)}</p>
                    )}
                  </div>

                  {!!product.purchase_entry_price && (
                    <span className="rounded-2xl bg-red-50 px-4 py-2 text-blue-500">
                      Purchase Entry Price:{' '}
                      {formatCurrency(product.purchase_entry_price)}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-end justify-between gap-6">
                <div className="flex shrink-0 gap-4 whitespace-nowrap text-body-sm font-semibold">
                  {hasProductStock && !product.is_auction ? (
                    <button
                      className="rounded-full bg-black px-6 py-3 text-white"
                      onClick={handlePromote}
                    >
                      Promote
                    </button>
                  ) : null}
                  {hasProductStock ? (
                    <Link
                      href={`/edit-listing/${product.id}`}
                      className="rounded-full border border-[#7f7f7f] px-6 py-3"
                    >
                      Edit listing
                    </Link>
                  ) : null}
                  <Link
                    className="rounded-full border border-[#7f7f7f] px-6 py-3"
                    href={{
                      pathname: '/create-listing/',
                      query: { id: product.id, step: 3 },
                    }}
                  >
                    Duplicate
                  </Link>
                  <button
                    className="rounded-full border border-[#7f7f7f] px-6 py-3"
                    onClick={handleDeleteListing}
                  >
                    Delete
                  </button>
                  {isVehicleCategory(product.category) && product.bumps > 0 ? (
                    <button
                      className="rounded-full border border-[#7f7f7f] px-6 py-3"
                      onClick={handleProductBump}
                    >
                      Bump
                    </button>
                  ) : null}
                </div>
                <div className="flex shrink-0 flex-col items-end gap-6 text-body-sm font-medium">
                  {product.purchase_entry_id ? (
                    <PurchaseEntryLink entryId={product.purchase_entry_id} />
                  ) : null}
                  {Boolean(product.date_added) ? (
                    <span className="whitespace-nowrap text-[#7f7f7f]">
                      Listed on {formatListedDate(product.date_added)}
                    </span>
                  ) : null}
                  <div className="flex items-center gap-12 text-body-sm font-medium">
                    {views?.views ? (
                      <span className="flex items-center gap-2">
                        <Eye className="size-8 text-[#7f7f7f]" />
                        <span>{views.views + 1}</span>
                      </span>
                    ) : null}
                    <span className="flex items-center gap-2">
                      <Heart className="size-8 text-[#7f7f7f]" />
                      <span>{product.favorite_count}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface Filters {
  category: string | 'all';
  category1: string | 'all';
  condition: Condition | 'all';
  availability: Availability;
  brand: string | 'all';
  auctionName?: string | 'all';
}

interface ListingFiltersProps {
  filters: Filters;
  setAvailability: (availability: Availability) => void;
  setAuction: (auction: string) => void;
}

const ListingFilters = ({
  filters,
  setAvailability,
  setAuction,
}: ListingFiltersProps) => {
  const { userDoc } = useAuth();

  const availabilityPills: { label: string; value: Availability }[] = [
    { label: 'Listings', value: 'all' },
    { label: 'On Sale', value: 'onSale' },
    { label: 'Sold', value: 'sold' },
    { label: 'Drafts', value: 'draft' },
    { label: 'Auctions', value: 'auction' },
  ];

  return (
    <div>
      <div className="flex w-full gap-4 overflow-x-auto text-nowrap text-body-sm font-medium scrollbar-none">
        {availabilityPills.map((pill) => {
          const active = filters.availability === pill.value;
          return (
            <button
              key={pill.value}
              onClick={() => setAvailability(pill.value)}
              className={`shrink-0 rounded-full px-10 py-4 ${
                active
                  ? 'bg-black text-white'
                  : 'border border-[#d9d9d9] bg-white'
              }`}
            >
              {pill.label}
            </button>
          );
        })}
      </div>
      {userDoc?.roles?.auctions && (
        <div className="mt-8 flex w-min gap-[1.6rem] pl-5">
          <span className="flex items-center whitespace-nowrap font-semibold">
            Auction Name:{' '}
          </span>
          <Select
            options={[
              {
                label: 'No Auction Filter',
                value: 'all',
                id: 'all',
              },
              {
                label: 'Black Friday 2023',
                value: 'Black Friday 2023',
                id: 'Black Friday 2023',
              },
              {
                label: 'Brian Barnes Washougal 2023',
                value: 'Brian Barnes Washougal 2023',
                id: 'Brian Barnes Washougal 2023',
              },
              {
                label: 'Brian Barnes Ironman',
                value: 'Brian Barnes Ironman',
                id: 'Brian Barnes Ironman',
              },
              {
                label: 'Veteran MX 2024 Auction',
                value: 'Veteran MX 2024 Auction',
                id: 'Veteran MX 2024 Auction',
              },
              {
                label: 'Brian Barnes 2024',
                value: 'Brian Barnes 2024',
                id: 'Brian Barnes 2024',
              },
              {
                label: 'Matt Buyten',
                value: 'Matt Buyten',
                id: 'Matt Buyten',
              },
            ]}
            allowEmpty={false}
            placeholder="No Auction Filter"
            value={filters.auctionName}
            onChange={(option) => {
              setAuction(option.id);
            }}
          />
        </div>
      )}
    </div>
  );
};

const ListingCardSkeleton = () => (
  <div className="animate-pulse">
    {/* mobile — mirrors the mobile ListingCardList */}
    <div className="block lg:hidden">
      <div className="flex flex-col gap-6 rounded-[24px] bg-white p-6">
        <div className="flex gap-6">
          <div className="size-[100px] shrink-0 rounded-[16px] bg-gray-200" />
          <div className="flex flex-1 flex-col gap-2">
            <div className="h-5 w-4/5 rounded bg-gray-200" />
            <div className="h-4 w-3/5 rounded bg-gray-200" />
            <div className="h-4 w-2/5 rounded bg-gray-200" />
            <div className="h-6 w-1/3 rounded bg-gray-200" />
          </div>
          <div className="h-9 w-4 rounded bg-gray-200" />
        </div>
        <div className="flex items-center justify-between">
          <div className="h-10 w-44 rounded-xl bg-gray-200" />
          <div className="h-4 w-48 rounded bg-gray-200" />
        </div>
      </div>
    </div>

    {/* desktop — mirrors the desktop ListingCardList */}
    <div className="hidden w-full gap-6 rounded-[24px] bg-white p-4 lg:flex">
      <div className="size-[200px] shrink-0 rounded-[16px] bg-gray-200" />
      <div className="flex w-full flex-col justify-between p-4">
        <div className="flex flex-col gap-4">
          <div className="h-6 w-2/3 rounded bg-gray-200" />
          <div className="h-4 w-40 rounded bg-gray-200" />
          <div className="h-7 w-28 rounded bg-gray-200" />
        </div>
        <div className="flex items-center justify-between gap-6">
          <div className="flex gap-4">
            <div className="h-12 w-28 rounded-full bg-gray-200" />
            <div className="h-12 w-28 rounded-full bg-gray-200" />
            <div className="h-12 w-24 rounded-full bg-gray-200" />
          </div>
          <div className="h-5 w-32 rounded bg-gray-200" />
        </div>
      </div>
    </div>
  </div>
);

export const ListingsPage = () => {
  const { userDoc } = useAuth();

  const canExport = [
    'tBZE37lB7ygQY7N8Jj17Josf9e52', // CyclemaxParts
    'FNFrXW24lIXK0wI1Gf80LXyTuRy2', // MotorPsychoSport
  ].includes(userDoc?.uid ?? '');

  const queryClient = useQueryClient();
  const searchParams = useSearchParams();

  const [search, setSearch] = useState(searchParams?.get('q') ?? '');
  const [_, setQSearch] = useQueryState('q', {
    history: 'replace',
  });
  const debouncedSearchTerm = useDebounce(search, 500);
  useEffect(() => {
    setQSearch(debouncedSearchTerm);
  }, [debouncedSearchTerm, setQSearch]);

  const [smartPricingModalOpen, setSmartPricingModalOpen] = useState(false);
  const [smartPricingOffModalOpen, setSmartPricingOffModalOpen] =
    useState(false);

  const [deleteListingModalOpen, setDeleteListingModalOpen] = useState(false);
  const [priceDropModalOpen, setPriceDropModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] =
    useState<ProductDocument | null>(null);
  const [deletedProducts, setDeletedProducts] = useState<string[]>([]);
  const { showSuccessToast, showErrorToast } = useToastContext();

  const [sortModalIsOpen, toggleSortModalIsOpen] = useReducer(
    (prev) => !prev,
    false
  );
  const [sort, setSort] = useState<string | null>(null);
  const [promoteModalIsOpen, setPromoteModalIsOpen] = useState(false);
  const [actionsDrawerOpen, setActionsDrawerOpen] = useState(false);

  const [page, setPage] = useQueryState('page', {
    history: 'push',
    ...parseAsInteger.withDefault(1),
  });
  const [category] = useQueryState('category', {
    history: 'push',
    ...parseAsString.withDefault('all'),
  });
  const [category1] = useQueryState('category1', {
    history: 'push',
    ...parseAsString.withDefault('all'),
  });
  const [brand] = useQueryState('brand', {
    history: 'push',
    ...parseAsString.withDefault('all'),
  });
  const [condition] = useQueryState('condition', {
    history: 'push',
    ...parseAsString.withDefault('all'),
  });
  const [availability, setAvailability] = useQueryState('availability', {
    history: 'push',
    ...parseAsString.withDefault('all'),
  });
  const [auction, setAuction] = useQueryState('auction', {
    history: 'push',
    ...parseAsString.withDefault('all'),
  });
  const [limit, setLimit] = useQueryState('limit', {
    history: 'push',
    ...parseAsInteger.withDefault(12),
  });

  const { data, isLoading } = useQuery({
    queryKey: [
      'listings',
      page,
      category,
      category1,
      brand,
      condition,
      availability,
      userDoc?.uid,
      limit,
      auction,
    ],
    queryFn: () =>
      getSellerListingsPaginated(
        userDoc?.uid ?? '',
        userDoc?.roles?.admin ?? false,
        page,
        category,
        category1,
        brand,
        condition as Condition,
        availability as Availability,
        auction,
        limit
      ),
    enabled: !debouncedSearchTerm,
  });

  const filters = useMemo(() => {
    let filterString = '';
    if (!(userDoc?.roles?.admin || userDoc?.roles?.support)) {
      filterString += `seller_id:=${userDoc?.uid} && `;
    }
    if (category !== 'all') {
      filterString += `category:${category} && `;
    }
    if (category1 !== 'all') {
      filterString += `category1:${category1} && `;
    }
    if (brand !== 'all') {
      filterString += `brand:${brand} && `;
    }
    if (condition !== 'all') {
      // if its new , filter by new, else filter by not new
      if (condition === 'New') {
        filterString += `condition:"${condition}" && `;
      } else {
        filterString += `condition:!="New" && `;
      }
    }
    if (availability === 'onSale') {
      filterString += `on_sale:true && `;
    }
    if (availability === 'auction') {
      filterString += `is_auction:true && `;
    }
    if (filterString.endsWith('&& ')) {
      filterString = filterString.slice(0, -4);
    }

    return filterString;
  }, [
    userDoc?.roles?.admin,
    userDoc?.roles?.support,
    userDoc?.uid,
    category,
    category1,
    brand,
    condition,
    availability,
  ]);

  const { data: searchResults = [], isLoading: isSearchLoading } = useQuery({
    queryKey: ['listingsSearch', debouncedSearchTerm, filters, userDoc?.uid],
    queryFn: () =>
      searchSellerProductsServer({
        q: debouncedSearchTerm,
        filter_by: filters || undefined,
      }),
    enabled: !!debouncedSearchTerm,
  });

  const products = useMemo(() => {
    const _products = (data?.results ?? []).filter(
      (product) => !deletedProducts.some((id) => id === product.id)
    );

    const sorted = [..._products].sort((a, b) => {
      switch (sort) {
        case 'Newest listings':
          return (b.created ?? 0) - (a.created ?? 0);
        case 'Most Favorited':
          return (b.favorite_count ?? 0) - (a.favorite_count ?? 0);
        case 'Alphabetically (A-Z)':
          return a.title.localeCompare(b.title);
        case 'Highest Price':
          return b.price - a.price;
        case 'Lowest Price':
          return a.price - b.price;
        default:
          return 0;
      }
    });

    return sorted;
  }, [data?.results, deletedProducts, sort]);

  const sortOptions = SORT_OPTIONS.map((op) => ({
    id: op,
    label: op,
    value: op,
  }));

  const pageSizeOptions = [12, 24, 48, 96].map((n) => ({
    id: String(n),
    label: `${n} per page`,
    value: String(n),
  }));

  const updateFloorPrice = async (
    floorPrice: number | undefined,
    product: ProductDocument
  ) => {
    try {
      const keys = productSchema.pick({
        floor_price: true,
      });
      if (floorPrice) {
        const res = keys.safeParse({ floor_price: floorPrice });
        if (!res.success) {
          logError(res.error.message);
          throw new Error('Unable to update product. Please contact support.');
        }
      }
      const updates = {
        floor_price: floorPrice ? floorPrice : deleteField(),
        date_updated: Date.now(),
      };
      await updateProductByKeys(product.id, updates);
      setSmartPricingModalOpen(false);
      setSmartPricingOffModalOpen(false);

      queryClient.invalidateQueries({
        queryKey: ['listings'],
      });
      showSuccessToast('Floor price updated');
    } catch (e) {
      showErrorToast((e as Error).message);
    }
  };

  return (
    <div className="relative flex flex-col px-8 lg:px-24 xl:px-60">
      <div className="z-50">
        {!!selectedProduct && (
          <>
            <SmartPricingModal
              open={smartPricingModalOpen}
              setOpen={setSmartPricingModalOpen}
              product={selectedProduct}
              updateFloorPrice={updateFloorPrice}
              key={selectedProduct.id}
            />

            <SmartPricingOffModal
              open={smartPricingOffModalOpen}
              setOpen={setSmartPricingOffModalOpen}
              product={selectedProduct}
              updateFloorPrice={updateFloorPrice}
              key={selectedProduct.id + 'smartPricingOffModal'}
            />

            <DeleteListingModal
              open={deleteListingModalOpen}
              setOpen={setDeleteListingModalOpen}
              product={selectedProduct}
              key={selectedProduct.id + 'deleteListingModal'}
              onDelete={() => {
                queryClient.invalidateQueries({
                  queryKey: ['listings'],
                });
                // add the deleted product to the deleted products state for client side optimistic update
                setDeletedProducts([
                  ...deletedProducts,
                  selectedProduct.id ?? '',
                ]);
              }}
            />

            <PriceDropModal
              open={priceDropModalOpen}
              setOpen={setPriceDropModalOpen}
              product={selectedProduct}
              key={selectedProduct.id + 'priceDropModal'}
              onSuccess={(price: number) => {
                selectedProduct.price = price;
              }}
            />

            <PromoteModal
              open={promoteModalIsOpen}
              setOpen={setPromoteModalIsOpen}
              setSmartPricingModalOpen={setSmartPricingModalOpen}
              setPriceDropModalOpen={setPriceDropModalOpen}
            />

            <ListingActionsDrawer
              open={actionsDrawerOpen}
              dismiss={() => setActionsDrawerOpen(false)}
              product={selectedProduct}
              key={selectedProduct.id + 'actionsDrawer'}
              onSmartPricing={() => {
                setActionsDrawerOpen(false);
                if (selectedProduct.floor_price) {
                  setSmartPricingOffModalOpen(true);
                } else {
                  setSmartPricingModalOpen(true);
                }
              }}
              onPromote={() => {
                setActionsDrawerOpen(false);
                setPromoteModalIsOpen(true);
              }}
              onDelete={() => {
                setActionsDrawerOpen(false);
                setDeleteListingModalOpen(true);
              }}
              onBump={async () => {
                setActionsDrawerOpen(false);
                await bumpProduct(selectedProduct.id);
                queryClient.invalidateQueries({ queryKey: ['listings'] });
                showSuccessToast('Successfully bumped listing!');
              }}
            />
          </>
        )}
        <SortModal
          open={sortModalIsOpen}
          toggle={toggleSortModalIsOpen}
          onSelectSort={setSort}
          sortSelected={sort}
        />
      </div>
      <div className="mb-8 flex flex-col justify-between gap-y-6 pt-8 lg:flex-row lg:gap-y-0 lg:pt-0">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-heading-h4 font-bold">
            <span className="lg:hidden">Listings</span>
            <span className="hidden lg:inline">Sell</span>
          </h1>
          <Link
            href={'/create-listing'}
            className="flex shrink-0 items-center gap-3 rounded-full bg-brand-secondary px-8 py-4 text-body-md font-semibold text-white transition-all duration-150 hover:bg-brand-secondary-darker lg:hidden"
          >
            <PlusIcon className="size-8" />
            Add listing
          </Link>
        </div>
        <div className="flex flex-col gap-[1.6rem] sm:flex-row">
          <div className="flex w-full gap-5">
            <div className="flex flex-1 items-center gap-5 rounded-full border border-[#d9d9d9] bg-white px-6 py-4 font-medium text-black">
              <IoSearchOutline className="size-10" />
              <input
                type="text"
                className="h-full w-full border-none text-body-md font-medium outline-none "
                placeholder="Search your listing"
                value={search ?? ''}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
              />
            </div>
            <Link
              href={'/create-listing'}
              className="hidden shrink-0 items-center gap-3 rounded-full bg-brand-secondary px-8 py-4 text-body-md font-semibold text-white transition-all duration-150 hover:bg-brand-secondary-darker lg:flex"
            >
              <PlusIcon className="size-8" />
              Add listing
            </Link>
            <button
              type="button"
              onClick={toggleSortModalIsOpen}
              className="flex size-[44px] items-center justify-center rounded-full bg-light-grey lg:hidden"
            >
              <ChevronsUpDown className="size-10" />
            </button>
          </div>

          {canExport && (
            <Button
              type="tertiary"
              text="Export Listings to CSV"
              width="small"
              leadingIcon={<DownloadIcon />}
              onClick={async () => {
                if (userDoc) {
                  await exportListings({ userDoc });
                  queryClient.invalidateQueries({
                    queryKey: ['authUser'],
                  });
                }
              }}
            />
          )}
        </div>
      </div>

      <div className="mb-16 flex w-full flex-col items-start gap-6 lg:flex-row lg:justify-between">
        <ListingFilters
          filters={{
            category,
            category1,
            condition: condition as Condition,
            availability: availability as Availability,
            brand,
            auctionName: auction ?? undefined,
          }}
          setAvailability={setAvailability}
          setAuction={setAuction}
        />
        <div className="hidden shrink-0 items-center gap-4 lg:flex">
          <div className="w-[200px]">
            <Select
              options={sortOptions}
              allowEmpty={false}
              placeholder="Newest listings"
              value={sort ?? undefined}
              onChange={(option) => setSort(option.id)}
              rounded
            />
          </div>
          <div className="w-[170px]">
            <Select
              options={pageSizeOptions}
              allowEmpty={false}
              value={String(limit)}
              onChange={(option) => {
                setLimit(parseInt(option.id, 10));
                setPage(1);
              }}
              rounded
            />
          </div>
        </div>
      </div>
      {!debouncedSearchTerm ? (
        isLoading && products?.length === 0 ? (
          <div className="flex w-full flex-col gap-8">
            {[1, 2, 3, 4].map((i) => (
              <ListingCardSkeleton key={i} />
            ))}
          </div>
        ) : products.length > 0 ? (
          <div className="flex flex-col gap-16">
            <div className="flex flex-col gap-8">
              {products.map((product) => (
                <ListingCardList
                  key={product.id}
                  setSmartPricingModalOpen={setSmartPricingModalOpen}
                  product={product}
                  setSelectedProduct={setSelectedProduct}
                  setDeleteListingModalOpen={setDeleteListingModalOpen}
                  setSmartPricingOffModalOpen={setSmartPricingOffModalOpen}
                  setPromoteModalOpen={setPromoteModalIsOpen}
                  setActionsDrawerOpen={setActionsDrawerOpen}
                />
              ))}
            </div>

            <PaginatorClient
              currentPage={page}
              onPageChange={(page) => {
                setPage(page);
              }}
              totalPages={data?.pageInfo.totalPages ?? 1}
            />
          </div>
        ) : (
          <div className="mt-4 flex h-full w-full grow flex-col items-center justify-center py-32">
            <div className="flex flex-col items-center gap-8">
              <SafeImage
                src={EmptyBox}
                alt="empty box"
                width={100}
                height={100}
              />
              <h1 className="text-heading-h4 font-bold">No listings yet</h1>

              <span className="text-body-sm font-medium text-text-muted">
                Add your first item and start earning cash
              </span>

              <Link
                href={'/create-listing'}
                className="translation-all flex cursor-pointer items-center gap-4 rounded-full border-none bg-brand-secondary px-10 py-5 text-body-md font-semibold text-white duration-150 hover:bg-brand-secondary-darker"
              >
                <PlusIcon className="size-10" />
                Add listing
              </Link>
            </div>
          </div>
        )
      ) : isSearchLoading ? (
        <div className="mx-auto mt-[1.6rem] flex h-12 w-12 items-center justify-center py-32">
          <Spinner />
        </div>
      ) : (
        <SearchResults
          hits={searchResults}
          setSelectedProduct={setSelectedProduct}
          setSmartPricingModalOpen={setSmartPricingModalOpen}
          setDeleteListingModalOpen={setDeleteListingModalOpen}
          setSmartPricingOffModalOpen={setSmartPricingOffModalOpen}
          setPromoteModalOpen={setPromoteModalIsOpen}
          setActionsDrawerOpen={setActionsDrawerOpen}
          deletedProducts={deletedProducts}
        />
      )}
    </div>
  );
};

function SearchResults({
  hits,
  setSelectedProduct,
  setSmartPricingModalOpen,
  setDeleteListingModalOpen,
  setSmartPricingOffModalOpen,
  setPromoteModalOpen,
  setActionsDrawerOpen,
  deletedProducts,
}: {
  hits: ProductDocument[];
  setSelectedProduct: React.Dispatch<
    React.SetStateAction<ProductDocument | null>
  >;
  setSmartPricingModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setDeleteListingModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSmartPricingOffModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setPromoteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setActionsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  deletedProducts: string[];
}) {
  const filteredHits = hits.filter(
    (hit) => !deletedProducts.some((id) => id === hit.id)
  );

  return filteredHits.length > 0 ? (
    <div className="flex flex-col gap-8">
      {filteredHits.map((product) => (
        <ListingCardList
          key={product.id}
          product={product}
          setSelectedProduct={setSelectedProduct}
          setSmartPricingModalOpen={setSmartPricingModalOpen}
          setDeleteListingModalOpen={setDeleteListingModalOpen}
          setSmartPricingOffModalOpen={setSmartPricingOffModalOpen}
          setPromoteModalOpen={setPromoteModalOpen}
          setActionsDrawerOpen={setActionsDrawerOpen}
        />
      ))}
    </div>
  ) : (
    <div className="mx-auto mt-[1.6rem] w-[50rem] py-32">
      <EmptyState
        icon={<IoDocumentTextOutline className="h-32 w-32" />}
        title={'No listings matching your search results'}
        description={'Draft / Sold listings cannot be searched.'}
      />
    </div>
  );
}
